import { catchError, filter, map, Observable, of, switchMap, take, throwError } from 'rxjs'

import type { CartFragment } from '../../../graphql/magento'
import { isMagentoScopeNotGrantedError } from '../../auth/errors'
import { latestAccessTokenFactory$ } from '../../auth/state'
import { CartMirrorState, cartState$ } from './cart'

/**
 * The cart a mutation should be sent against, with the token that cart's type accepts.
 */
export interface CartMutationTarget {
  cart: CartFragment
  token: string | undefined
}

export interface CartMutationTargetOptions {
  /** rethrow instead of sending the mutation unauthenticated */
  loginRequired?: boolean
}

/**
 * A cart no longer being rewritten: loading has finished and no guest-to-customer merge is running.
 *
 * `loading` alone is not enough. Magento's `mergeCarts` deactivates the guest quote once it has read
 * it, so a mutation that lands after that read is written to a dead quote and silently stranded
 * rather than rejected. Gating on `mergeStatus` closes that window instead of narrowing it.
 */
export const isCartSettled = ({ loading, mergeStatus }: CartMirrorState): boolean =>
  !loading && mergeStatus !== 'loading'

export const cartSettled$: Observable<CartMirrorState> = cartState$.pipe(filter(isCartSettled))

/**
 * Resolve the access token as of now, refreshing it if it has expired.
 *
 * Uses the token *factory* rather than `latestAccessToken$`: the factory is the observable form of
 * `getAccessTokenSilently`, so this matches what the hook did before and does not hand Magento a
 * token that expired while the shopper was on the page.
 *
 * `allowInteractive: false` because the factory otherwise escalates a `consent_required` failure to
 * `getAccessTokenWithPopup`. The hook this replaces called `getAccessTokenSilently` directly and
 * degraded to no token, and a mutation on the add-to-cart path is the wrong place to raise a popup
 * the shopper did not ask for — one a blocker may swallow, leaving the add to hang on a dialog that
 * never opened.
 */
const currentAccessToken$ = ({ loginRequired }: CartMutationTargetOptions) =>
  latestAccessTokenFactory$.pipe(
    take(1),
    switchMap((getAccessToken) => getAccessToken({ allowInteractive: false })),
    map((token): string | undefined => token),
    catchError((error: unknown) => (loginRequired ? throwError(() => error) : of(undefined))),
  )

/**
 * Emit the cart id paired with the token valid for that cart, once the cart has settled.
 *
 * Three properties follow from sequencing rather than reacting to flags: a mutation waits for a
 * settled cart instead of racing the merge, the cart id and token are paired at emission so no call
 * site can pair them wrongly, and the pair is read fresh each time rather than pinned to the first
 * settled render the way the `usePromisify` deferred did.
 */
export const cartMutationTarget$ = (
  options: CartMutationTargetOptions = {},
): Observable<CartMutationTarget> =>
  cartSettled$.pipe(
    take(1),
    switchMap(({ cart, type, error }) => {
      if (!cart) {
        // a cart error explains why there is no cart, so report it in preference to the generic
        // message; a missing `magento_user` scope is not fatal and never produced a cart to begin with
        return throwError(() =>
          error && !isMagentoScopeNotGrantedError(error)
            ? error
            : new Error(`Unable to load cart for mutation`),
        )
      }
      if (type === 'customer') {
        return currentAccessToken$(options).pipe(map((token) => ({ cart, token })))
      }
      // A guest quote has no customer to authorize, and sending a token against one is exactly the
      // ownership error this pairing exists to prevent (sc-142551) — but a caller that requires a
      // login cannot be served by one at all, so reject rather than proceed unauthenticated. The
      // cart has settled by here, so a signed-in shopper's cart is already the merged customer cart
      // and this is a genuine absence of a customer rather than the pre-merge window.
      return options.loginRequired
        ? throwError(() => new Error(`Cart mutation requires a signed-in customer`))
        : of({ cart, token: undefined })
    }),
  )
